import React, { useState, useRef, useEffect, useMemo } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  badge?: string;
  icon?: string;
  isActive?: boolean;
}

export interface SearchableMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  icon?: string;
  direction?: 'auto' | 'down' | 'up';
  maxDisplayedChips?: number;
  /** Footer text while nothing is selected */
  noneSelectedText?: string;
}

export function SearchableMultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select options...',
  searchPlaceholder = 'Search...',
  emptyText = 'No options available',
  disabled = false,
  className = '',
  icon,
  direction = 'down',
  maxDisplayedChips = 2,
  noneSelectedText = 'No restrictions (All allowed)',
}: SearchableMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openUpwards, setOpenUpwards] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Map of options by value for fast lookup
  const optionsMap = useMemo(() => {
    const map = new Map<string, MultiSelectOption>();
    options.forEach((opt) => map.set(opt.value, opt));
    return map;
  }, [options]);

  const selectedOptions = useMemo(() => {
    return value.map((val) => optionsMap.get(val) || { value: val, label: val });
  }, [value, optionsMap]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return options;
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(term) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(term))
    );
  }, [options, searchTerm]);

  // Detect available space to flip upwards only if direction is 'up' or 'auto'
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    if (direction === 'down') {
      setOpenUpwards(false);
      return;
    }
    if (direction === 'up') {
      setOpenUpwards(true);
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (spaceBelow < 260 && spaceAbove > 320) {
      setOpenUpwards(true);
    } else {
      setOpenUpwards(false);
    }
  }, [isOpen, direction]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input on open and ensure dropdown is scrolled into view
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTimeout(() => {
        searchInputRef.current?.focus();
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [isOpen]);

  const toggleOption = (optVal: string) => {
    if (value.includes(optVal)) {
      onChange(value.filter((v) => v !== optVal));
    } else {
      onChange([...value, optVal]);
    }
  };

  const removeSingle = (optVal: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optVal));
  };

  const handleSelectAllVisible = () => {
    const visibleVals = filteredOptions.map((o) => o.value);
    const combined = Array.from(new Set([...value, ...visibleVals]));
    onChange(combined);
  };

  const handleDeselectAllVisible = () => {
    const visibleVals = new Set(filteredOptions.map((o) => o.value));
    onChange(value.filter((v) => !visibleVals.has(v)));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  const allVisibleSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => value.includes(opt.value));

  return (
    <div
      ref={containerRef}
      className={`relative w-full text-left ${isOpen ? 'z-[100]' : 'z-auto'} ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        className={`w-full min-h-[40px] px-3 py-1.5 rounded-lg border text-xs flex items-center justify-between gap-2 transition-all cursor-pointer select-none ${
          disabled
            ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
            : isOpen
            ? 'bg-white border-[#4f46e5] ring-2 ring-[#4f46e5]/20 shadow-xs'
            : 'bg-white border-[#e2e8f0] hover:border-slate-400 text-slate-800 shadow-2xs'
        }`}
      >
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0 py-0.5">
          {icon && (
            <i className={`${icon} text-slate-400 shrink-0 text-sm mr-0.5`} />
          )}

          {value.length === 0 ? (
            <span className="text-slate-400 font-normal truncate">{placeholder}</span>
          ) : (
            <>
              {selectedOptions.slice(0, maxDisplayedChips).map((opt) => (
                <span
                  key={opt.value}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold shrink-0"
                >
                  <span className="truncate max-w-[130px]">{opt.label}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => removeSingle(opt.value, e)}
                      className="p-0.5 hover:bg-indigo-100 rounded text-indigo-600 hover:text-indigo-900 cursor-pointer"
                      title="Remove"
                    >
                      <i className="ri-close-line text-xs leading-none" />
                    </button>
                  )}
                </span>
              ))}

              {value.length > maxDisplayedChips && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-semibold shrink-0">
                  +{value.length - maxDisplayedChips} more
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0 text-slate-400 ml-1">
          {value.length > 0 && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              title="Clear all"
              className="p-1 hover:text-red-500 hover:bg-slate-100 rounded cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xs" />
            </button>
          )}
          <i
            className={`ri-arrow-down-s-line text-sm transition-transform duration-150 ${
              isOpen ? 'rotate-180 text-[#4f46e5]' : 'text-slate-400'
            }`}
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute left-0 w-full min-w-[280px] bg-white border border-slate-200 rounded-xl shadow-2xl z-[999] overflow-hidden animate-in fade-in-50 zoom-in-95 duration-100 ${
            openUpwards ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
        >
          {/* Search Box */}
          <div className="p-2 border-b border-slate-100 bg-slate-50/80 sticky top-0 z-10">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-8 pl-8 pr-7 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] shadow-2xs"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              )}
            </div>
          </div>

          {/* Action Bar (Count + Select All / Clear) */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50/50 border-b border-slate-100 text-[11px]">
            <span className="text-slate-500 font-medium">
              {value.length} of {options.length} selected
            </span>
            <div className="flex items-center gap-2">
              {options.length > 0 && (
                <button
                  type="button"
                  onClick={allVisibleSelected ? handleDeselectAllVisible : handleSelectAllVisible}
                  className="text-[#4f46e5] hover:text-indigo-800 font-semibold cursor-pointer transition-colors"
                >
                  {allVisibleSelected ? 'Deselect All' : 'Select All'}
                </button>
              )}
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-56 overflow-y-auto divide-y divide-slate-50 p-1 scrollbar-thin">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">
                <i className="ri-search-line text-slate-300 text-base mb-1 block" />
                {options.length === 0 ? emptyText : `No matches for "${searchTerm}"`}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = value.includes(opt.value);

                return (
                  <div
                    key={opt.value}
                    onClick={() => toggleOption(opt.value)}
                    className={`px-3 py-2 rounded-lg cursor-pointer flex items-center justify-between gap-2.5 text-xs transition-colors ${
                      isSelected
                        ? 'bg-indigo-50/60 text-[#4f46e5]'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate flex-1 min-w-0">
                      {/* Checkbox */}
                      <div
                        className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-[#4f46e5] text-white shadow-xs'
                            : 'border border-slate-300 bg-white hover:border-[#4f46e5]'
                        }`}
                      >
                        {isSelected && <i className="ri-check-line text-xs font-bold leading-none" />}
                      </div>

                      <div className="truncate">
                        <span className={`block truncate ${isSelected ? 'font-semibold text-[#1e293b]' : 'font-normal'}`}>
                          {opt.label}
                        </span>
                        {opt.sublabel && (
                          <span className="block text-[11px] text-slate-400 truncate">
                            {opt.sublabel}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {opt.badge && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                          {opt.badge}
                        </span>
                      )}
                      {opt.isActive === false && (
                        <span className="text-[10px] text-amber-600 italic">
                          (inactive)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Bar */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/90">
            <span className="text-[11px] text-slate-400">
              {value.length === 0 ? noneSelectedText : `${value.length} selected`}
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 rounded-md bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors cursor-pointer shadow-xs"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableMultiSelect;
